import {createContext, memo, ReactNode, useContext} from 'react'
import type {GlobalPreferencesResponse} from '../helpers/customGlobalPreferences'
import {invariant} from '../helpers/invariant'
import {Mapping, InferredPreferences} from './site-preferences.types'
import {transformPreferences} from '../helpers/transformPreferences'
import React from 'react'

/*
 * !!! DANGER !!!
 * Exposing global preference mappings will leak it to the browser (client side)
 * Add more groups and their mappings here as needed
 *
 * Usage is identical to site-preferences — see site-preferences.tsx for examples.
 * The only difference is the API endpoint (global-custom-preferences vs site-custom-preferences).
 */

export const GLOBAL_PREFERENCE_MAPPING = {
    TPA: {
        TPAConfig: {
            id: 'TPAConfig',
            type: 'string',
            defaultValue: '{}'
        }
    }
} as const satisfies Mapping

// Type-safe client global preferences inferred from the mapping
export type ClientGlobalPrefs = InferredPreferences<typeof GLOBAL_PREFERENCE_MAPPING>

// #region Context
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const ClientGlobalPreferencesContext = createContext<ClientGlobalPrefs>(undefined!)
const ClientGlobalPreferencesProvider = ClientGlobalPreferencesContext.Provider
export const useClientGlobalPreferences = (): ClientGlobalPrefs => {
    const prefs = useContext(ClientGlobalPreferencesContext)
    invariant(
        prefs,
        'useClientGlobalPreferences must be used within a ClientGlobalPreferencesProvider'
    )
    return prefs
}
export const ClientGlobalPreferences = memo(
    ({preferences, children}: {preferences: ClientGlobalPrefs; children: ReactNode}) => {
        return (
            <ClientGlobalPreferencesProvider value={preferences}>
                {children}
            </ClientGlobalPreferencesProvider>
        )
    }
)
ClientGlobalPreferences.displayName = 'ClientGlobalPreferences'
// #endregion Context

export const toClientGlobalPreferences = (prefs: GlobalPreferencesResponse): ClientGlobalPrefs => {
    return transformPreferences<ClientGlobalPrefs>(
        prefs,
        GLOBAL_PREFERENCE_MAPPING as Mapping,
        'Global'
    )
}
